import React, { useMemo, useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { mainCategories } from "../../data/resourceCategories";
import useSearchResults from "../../hooks/search/useSearchResults";
import SearchResults from "./SearchResults/SearchResults";
import { selectEffectiveCoordinates } from "../../slices/locationSlice";
import AnimatedLoadingScreen from "../../components/UIFeedback/AnimatedLoadingScreen/AnimatedLoadingScreen";
import ErrorScreen from "../../components/UIFeedback/ErrorScreen/ErrorScreen";

// Helper to flatten all categories for lookup
const flattenCategories = (categories, parentKey = null) => {
  return categories.flatMap((category) => {
    const flattened = [{ ...category, parentKey }];
    if (category.subcategories) {
      flattened.push(...flattenCategories(category.subcategories, category.key));
    }
    return flattened;
  });
};

// Recursive helper to find top-level parent of a deeply nested key
const findTopLevelCategory = (categories, keyToFind) => {
  for (const category of categories) {
    if (category.key === keyToFind) return category;

    if (category.subcategories) {
      const subMatch = category.subcategories.find((sub) => sub.key === keyToFind);
      if (subMatch) return category;

      for (const sub of category.subcategories) {
        if (sub.subcategories) {
          const deepMatch = sub.subcategories.find((deep) => deep.key === keyToFind);
          if (deepMatch) return category;

          const recursive = findTopLevelCategory(sub.subcategories, keyToFind);
          if (recursive) return category;
        }
      }
    }
  }

  return null;
};

const SearchParent = React.memo(() => {
  const { t } = useTranslation();
  const location = useLocation();
  const { category, subcategory } = useParams();
  const effectiveCoordinates = useSelector(selectEffectiveCoordinates);
  const selectedQueryKeyword = useSelector((state) => state.search.queryKeyword);
  const [queryKeyword, setQueryKeyword] = useState(null);

  const flattenedCategories = useMemo(() => flattenCategories(mainCategories), []);

  const selectedCategory = useMemo(() => {
    // 1. From location.state
    if (location.state?.activeCategory?.key) {
      return flattenedCategories.find((cat) => cat.key === location.state.activeCategory.key);
    }

    // 2. If subcategory present, derive mainCategory from it
    if (category && subcategory) {
      return findTopLevelCategory(mainCategories, category);
    }

    // 3. Fallback to flat match
    return flattenedCategories.find((cat) => cat.key === category);
  }, [category, subcategory, location, flattenedCategories]);

  const selectedSubcategory = useMemo(() => {
    if (!selectedCategory?.subcategories || !subcategory) return null;

    const findSub = (subs, key) => {
      for (const sub of subs) {
        if (sub.key === key) return sub;
        if (sub.subcategories) {
          const nested = findSub(sub.subcategories, key);
          if (nested) return nested;
        }
      }
      return null;
    };

    return findSub(selectedCategory.subcategories, subcategory);
  }, [subcategory, selectedCategory]);

  const parentCategory = useMemo(() => {
    if (!selectedCategory?.parentKey) return null;
    return flattenedCategories.find((cat) => cat.key === selectedCategory.parentKey);
  }, [selectedCategory, flattenedCategories]);

  useEffect(() => {
    const keyword =
      selectedQueryKeyword ||
      location.state?.activeCategory?.queryKeyword ||
      selectedSubcategory?.queryKeyword ||
      selectedCategory?.queryKeyword ||
      selectedSubcategory?.key ||
      selectedCategory?.key;

    setQueryKeyword(keyword || "default-key");
  }, [selectedQueryKeyword, selectedCategory, selectedSubcategory, location, category, subcategory]);

  const { data, loading, error } = useSearchResults(queryKeyword, effectiveCoordinates);

  if (!effectiveCoordinates) {
    return <AnimatedLoadingScreen message={t('fetching_location')} />;
  }

  if (!selectedCategory) {
    return (
      <ErrorScreen>
        <p>{t('invalid_category')}</p>
      </ErrorScreen>
    );
  }

  if (loading) {
    return <AnimatedLoadingScreen message={`${t('loading_results_for')} ${queryKeyword}...`} />;
  }

  if (error) {
    return (
      <ErrorScreen>
        {t('error_fetching_results_for')} {queryKeyword}. {t('please_try_again_later')}
      </ErrorScreen>
    );
  }

  return (
    <SearchResults
      data={data}
      category={selectedCategory.key}
      title={t(selectedSubcategory?.label || selectedCategory.label || parentCategory?.label)}
    />
  );
});

export default SearchParent;
